import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { useGetIndexChart } from '#/api/generated/indices/indices';
import { normalizeIndexChartData } from '#/components/charts/normalize';
import {
  getChartQueryPolicy,
  getChartTimeframeParams,
} from '#/components/charts/timeframes';
import { TimeseriesChart } from '#/components/charts/TimeseriesChart';
import type {
  ChartTimeframeId,
  ComparisonIndexId,
  TimeseriesPoint,
} from '#/components/charts/types';
import { DisclosureStatementButton } from '#/components/content/DisclosureStatement';
import { useIsBelowFoldInView } from '#/components/index/HomepageBelowFoldSection';
import { DropdownMenu } from '#/components/ui/DropdownMenu';
import { Icon } from '#/components/ui/icons';
import { ResponsiveIndicator } from '#/components/ui/ResponsiveIndicator';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { isSuccessResponse } from '#/lib/api-client';
import { DASH } from '#/utils/constants';
import { formatInteger, roundDecimal } from '#/utils/format';

type MarketIndexCardTimeframeId = Extract<ChartTimeframeId, '1Y' | '5Y'>;

interface MarketIndexCardDeltaBadge {
  percentChange: number;
}

interface MarketIndexCardValueChange {
  value: number;
  suffix: string;
}

export interface MarketIndexCardSummary {
  latestValue?: number | null;
  deltaBadge?: MarketIndexCardDeltaBadge | null;
  valueChange?: MarketIndexCardValueChange | null;
}

interface MarketIndexCardProps {
  title: string;
  timeframe: MarketIndexCardTimeframeId;
  summary: MarketIndexCardSummary;
  data: TimeseriesPoint[];
  isLoading?: boolean;
  isUnavailable?: boolean;
}

const MARKET_INDEX_TIMEFRAME_OPTIONS: Array<{
  id: MarketIndexCardTimeframeId;
  label: string;
}> = [
  { id: '5Y', label: '5 Years' },
  { id: '1Y', label: '1 Year' },
];

const MARKET_INDEXES: Array<{
  id: ComparisonIndexId;
  title: string;
}> = [
  { id: 'dow_jones', title: 'Dow Jones Industrial Average' },
  { id: 's_p_500', title: 'S&P 500' },
  { id: 'nasdaq', title: 'NASDAQ' },
];

function getTimeframeLabel(timeframe: MarketIndexCardTimeframeId) {
  return timeframe === '1Y' ? 'Over 1 Year' : 'Over 5 Years';
}

function formatLatestValue(value?: number | null) {
  return formatInteger(value) ?? DASH;
}

function TimeframeSelector({
  timeframe,
  onChange,
  trackContext,
}: {
  timeframe: MarketIndexCardTimeframeId;
  onChange: (nextTimeframe: MarketIndexCardTimeframeId) => void;
  /** Analytics grouping bucket (`data-track-context`) for the menu items. */
  trackContext?: string;
}) {
  const selectedLabel = MARKET_INDEX_TIMEFRAME_OPTIONS.find(
    (op) => op.id === timeframe,
  )?.label;

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger>
        <Icon id="plus-minus" size={12} className="text-fg-header shrink-0" />
        <span className="font-semibold">{selectedLabel}</span>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Positioner sideOffset={4}>
          <DropdownMenu.Popup>
            <DropdownMenu.Viewport>
              {MARKET_INDEX_TIMEFRAME_OPTIONS.map((option) => (
                <DropdownMenu.Item
                  key={option.id}
                  onClick={() => onChange(option.id)}
                  data-track="index_timeframe"
                  data-track-label={option.label}
                  data-track-context={trackContext}
                >
                  {option.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Viewport>
          </DropdownMenu.Popup>
        </DropdownMenu.Positioner>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MarketIndexCard({
  title,
  timeframe,
  summary,
  data,
  isLoading = false,
  isUnavailable = false,
}: MarketIndexCardProps) {
  const timeframeLabel = getTimeframeLabel(timeframe);

  return (
    <article className="border-divider-subtle bg-gray-10 p-gap flex h-full flex-col justify-between gap-20 border-b sm:rounded sm:border">
      <header className="flow flow-inner-8">
        <div>
          <h3 className="text-fg-header text-lg leading-tight font-semibold">
            {title}
          </h3>
          <p className="text-fg-muted mt-4 text-xs">{timeframeLabel}</p>
        </div>

        <div className="flex min-h-48 flex-wrap items-center gap-12">
          <p className="text-static-22 leading-none font-semibold text-slate-100">
            {isUnavailable && !summary.latestValue
              ? 'Unavailable'
              : formatLatestValue(summary.latestValue)}
          </p>

          {summary.deltaBadge ? (
            <ResponsiveIndicator
              type="percent"
              value={summary.deltaBadge.percentChange}
              suffix="%"
              defaultSize="md"
              sizeAtBreakpoints={[
                {
                  minWidth: 'lg',
                  size: 'sm',
                },
                {
                  minWidth: 'xl',
                  size: 'md',
                },
              ]}
            />
          ) : null}

          {summary.valueChange ? (
            <ResponsiveIndicator
              type="value"
              value={summary.valueChange.value}
              suffix={summary.valueChange.suffix}
              defaultSize="md"
              sizeAtBreakpoints={[
                {
                  minWidth: 'lg',
                  size: 'sm',
                },
                {
                  minWidth: 'xl',
                  size: 'md',
                },
              ]}
            />
          ) : null}
        </div>
      </header>

      <TimeseriesChart
        data={data}
        isLoading={isLoading}
        showAxes={false}
        showTabs={false}
        variant="compact"
      />
    </article>
  );
}

function MarketIndexCardSlot({
  indexId,
  title,
  timeframe,
  timeframeParams,
  queryPolicy,
}: {
  indexId: ComparisonIndexId;
  title: string;
  timeframe: MarketIndexCardTimeframeId;
  timeframeParams: ReturnType<typeof getChartTimeframeParams>;
  queryPolicy: ReturnType<typeof getChartQueryPolicy>;
}) {
  const isInView = useIsBelowFoldInView();

  const query = useGetIndexChart(indexId, timeframeParams, {
    query: {
      enabled: isInView,
      staleTime: queryPolicy.staleTime,
      gcTime: queryPolicy.gcTime,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: keepPreviousData,
    },
  });

  const response = isSuccessResponse(query.data) ? query.data : null;
  const isUnavailable = !response && !query.isFetching && !query.isPending;

  const data = useMemo(() => {
    return response ? normalizeIndexChartData(response.data) : [];
  }, [response]);

  const summary = useMemo<MarketIndexCardSummary>(() => {
    const latestPoint = data[data.length - 1];
    const latestValue = response?.data.current_price ?? latestPoint?.closePrice;
    const percentChange = roundDecimal(response?.data.price_change_percent);
    const valueChange = roundDecimal(response?.data.price_change_dollar);

    return {
      latestValue: latestValue ?? null,
      deltaBadge: percentChange == null ? null : { percentChange },
      valueChange:
        valueChange == null
          ? null
          : {
              value: valueChange,
              suffix: ` ${timeframe}`,
            },
    };
  }, [data, response, timeframe]);

  return (
    <MarketIndexCard
      title={title}
      timeframe={timeframe}
      summary={summary}
      data={data}
      isLoading={query.isFetching || query.isPending}
      isUnavailable={isUnavailable}
    />
  );
}

export function MarketIndexCardsSection() {
  const [timeframe, setTimeframe] = useState<MarketIndexCardTimeframeId>('5Y');
  const queryPolicy = getChartQueryPolicy(timeframe);
  const timeframeParams = getChartTimeframeParams(timeframe);

  return (
    <section
      className="col-feature flex flex-col gap-20 lg:gap-32"
      aria-label="Market index cards"
    >
      <SectionHeader
        title="Indexes and Securities"
        description="<p>Prices shown below are current as of market close yesterday. Check market feeds for live pricing.</p>"
        actions={<DisclosureStatementButton trackContext="market_index" />}
      />

      <div className="flow flow-inner-20">
        <div className="grid gap-20 lg:grid-cols-3">
          <TimeframeSelector
            timeframe={timeframe}
            onChange={setTimeframe}
            trackContext="market_index"
          />
        </div>

        <div className="-mx-gap grid sm:mx-0 sm:gap-20 lg:grid-cols-3">
          {MARKET_INDEXES.map((marketIndex) => (
            <MarketIndexCardSlot
              key={marketIndex.id}
              indexId={marketIndex.id}
              title={marketIndex.title}
              timeframe={timeframe}
              timeframeParams={timeframeParams}
              queryPolicy={queryPolicy}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
